import { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Plus, Minus, CalendarIcon, CheckCircle, XCircle, Users, ChevronLeft, Armchair, ShieldAlert, AlertTriangle, User, UserPlus, Loader2, StickyNote, Pencil, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, addDays, isSameDay, isToday, isBefore } from "date-fns";
import { fr } from "date-fns/locale";
import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability, TableBookablePeriod, TerracePreference } from "@/types/table";
import { isTableAvailableOnDate, isTableBookableOnDate, isTableInTemporaryBlockingPeriod } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds } from "@/lib/reservationConflictUtils";
import { findOptimalTable } from "@/lib/tableAssignmentUtils";
import { calculateEffectiveCapacity } from "@/lib/childrenCapacityUtils";
import { ChildrenSelector } from "@/components/ChildrenSelector";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import { ZonePreferenceSelector } from "@/components/ZonePreferenceSelector";
import { useBookableZones } from "@/hooks/useBookableZones";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";
import { generateAvailableTimes } from "@/lib/timeBookingUtils";
import { findParentSlotId, getTimeSlotsForDate, type TimeSlotDateOverride } from "@/lib/timeSlotUtils";
import { getStaffSlotsForDate } from "@/lib/zoneScheduleUtils";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { CustomerData } from "@/types/client";
import { useCustomerNotes } from "@/hooks/useCustomerNotes";
import { normalizePhoneForSearch } from "@/lib/clientUtils";
import { supabase } from "@/integrations/supabase/client";
import { CreateClientDialog } from "@/components/CreateClientDialog";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { loadCustomTableOrder, sortByCustomOrder, resolveTableOrder } from "@/lib/tableOrderUtils";
import { useToast } from "@/hooks/use-toast";

function parseFreeName(text: string): { firstName: string; lastName: string } {
  const trimmed = text.trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const spaceIdx = trimmed.indexOf(" ");
  if (spaceIdx === -1) return { firstName: trimmed, lastName: "" };
  return { firstName: trimmed.slice(0, spaceIdx), lastName: trimmed.slice(spaceIdx + 1) };
}

interface AvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  timeSlots: { start: string; end: string }[];
  allTimeSlots: { id?: string; start: string; end: string; availableDays?: number[] }[];
  dateOverrides?: TimeSlotDateOverride[];
  bookingMode: "slot" | "time";
  reservationDurationMinutes: number;
  reservationSettings?: {
    childrenEnabled?: boolean;
    strollerEnabled?: boolean;
    childrenCapacityMode?: string;
    terraceEnabled?: boolean;
    bookingIntervalMinutes?: number;
    minPeople?: number;
    zonesEnabled?: boolean;
  };
  establishmentClosures?: { startDate: string; endDate: string }[];
  isSlotClosedOnDate?: (slotId: string, dateStr: string) => boolean;
  isTerraceClosedOnDate?: (dateStr: string, slotId?: string) => boolean;
  existingClients?: CustomerData[];
  onReservationCreate?: (reservation: {
    customerFirstName: string;
    customerLastName: string;
    customerEmail?: string;
    customerPhone?: string;
    numberOfPeople: number;
    numberOfChildren?: number;
    hasStroller?: boolean;
    terracePreference?: TerracePreference;
    tableId?: string;
    combinedTableId?: string;
    startTime: string;
    date: Date;
    notes?: string;
    internalNotes?: string;
    durationMinutes?: number;
    source?: string;
    zonePreference?: string[];
  }) => Promise<void> | void;
  establishmentId?: string;
  maxPeople?: number;
  customAssignmentOrderEnabled?: boolean;
}

type Step = "search" | "results" | "booking";

export function AvailabilityDialog({
  open,
  onOpenChange,
  tables,
  combinedTables,
  reservations,
  availabilities,
  weekdayAvailabilities,
  bookablePeriods,
  timeSlots,
  allTimeSlots,
  dateOverrides = [],
  bookingMode,
  reservationDurationMinutes,
  reservationSettings,
  establishmentClosures,
  isSlotClosedOnDate,
  isTerraceClosedOnDate,
  existingClients = [],
  onReservationCreate,
  establishmentId,
  maxPeople,
  customAssignmentOrderEnabled = false,
}: AvailabilityDialogProps) {
  const { toast } = useToast();
  const { establishmentId: ctxEstablishmentId } = useEstablishment();
  const resolvedEstablishmentId = establishmentId || ctxEstablishmentId;
  const customTableOrder = useMemo(
    () => resolveTableOrder({ establishmentId: resolvedEstablishmentId, tables, combinedTables }),
    [resolvedEstablishmentId, tables, combinedTables, open]
  );
  const { customerNotes, getNotesForEmail, getCustomerIdForEmail, refetch: refetchCustomerNotes } = useCustomerNotes();

  const [step, setStep] = useState<Step>("search");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [guestCount, setGuestCount] = useState(2);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');
  const [zonePreference, setZonePreference] = useState<string[]>([]);
  const [customDuration, setCustomDuration] = useState<number | null>(null);
  const [includeUnderCapacity, setIncludeUnderCapacity] = useState(false);

  const { zones: bookableZones } = useBookableZones({ establishmentId: resolvedEstablishmentId, useAuthenticatedData: true });
  const activeZones = useMemo(() => bookableZones.filter(z => z.isActive), [bookableZones]);
  const zonesUiEnabled = (reservationSettings?.zonesEnabled ?? false) && activeZones.length > 0;
  const zoneAllows = <T extends { zoneId?: string | null }>(item: T): boolean => {
    if (!zonesUiEnabled || zonePreference.length === 0) return true;
    return item.zoneId != null && zonePreference.includes(item.zoneId as string);
  };

  // Booking step state
  const [selectedTableForBooking, setSelectedTableForBooking] = useState<{
    id: string;
    type: "table" | "combined";
    name: string;
    capacity: number;
  } | null>(null);
  // Client identification state (WalkIn-style autocomplete)
  const [selectedClient, setSelectedClient] = useState<CustomerData | null>(null);
  const [freeTextName, setFreeTextName] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [source, setSource] = useState("");
  const [editingCustomerNotes, setEditingCustomerNotes] = useState(false);
  const [customerNotesValue, setCustomerNotesValue] = useState("");
  const [savingCustomerNotes, setSavingCustomerNotes] = useState(false);
  const [showCreateClientDialog, setShowCreateClientDialog] = useState(false);

  const childrenEnabled = reservationSettings?.childrenEnabled ?? false;
  const strollerEnabled = reservationSettings?.strollerEnabled ?? false;
  const childrenCapacityMode = (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none';
  const terraceEnabled = reservationSettings?.terraceEnabled ?? false;
  const isTimeMode = bookingMode === 'time';
  const hasChildren = childrenEnabled && numberOfChildren > 0;
  const strollerActive = hasStroller && strollerEnabled;
  const effectiveDuration = customDuration ?? reservationDurationMinutes;

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        clientInputRef.current &&
        !clientInputRef.current.contains(e.target as Node)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const maxChildren = guestCount - 1;
    if (numberOfChildren > maxChildren) {
      setNumberOfChildren(Math.max(0, maxChildren));
    }
  }, [guestCount]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep("search");
      setSelectedDate(new Date());
      setGuestCount(2);
      setSelectedTimeSlot("");
      setIsCalendarOpen(false);
      setNumberOfChildren(0);
      setHasStroller(false);
      setTerracePreference('no_preference');
      setZonePreference([]);
      setCustomDuration(null);
      setIncludeUnderCapacity(false);
      setSelectedTableForBooking(null);
      resetBookingForm();
    }
    onOpenChange(newOpen);
  };

  const resetBookingForm = () => {
    setSelectedClient(null);
    setFreeTextName("");
    setShowClientDropdown(false);
    setNotes("");
    setInternalNotes("");
    setSource("");
    setEditingCustomerNotes(false);
    setCustomerNotesValue("");
  };

  const isDateClosed = (date: Date): boolean => {
    if (!establishmentClosures?.length) return false;
    const dateStr = formatDateLocal(date);
    return establishmentClosures.some(c => dateStr >= c.startDate && dateStr <= c.endDate);
  };

  const activeTables = useMemo(() => tables.filter(t => t.isActive && t.isBookable !== false), [tables]);
  const unbookableTables = useMemo(() => tables.filter(t => t.isActive && t.isBookable === false), [tables]);
  const activeCombined = useMemo(() => combinedTables.filter(ct => ct.isActive && ct.isBookable !== false), [combinedTables]);
  // Combinées actives mais hors réservation (isBookable=false) — assignation manuelle staff
  const unbookableCombined = useMemo(() => combinedTables.filter(ct => ct.isActive && ct.isBookable === false), [combinedTables]);

  const { zoneWindows } = useZoneTimeWindows({ establishmentId: resolvedEstablishmentId, useAuthenticatedData: true });
  const activeZoneIds = useMemo(() => new Set(activeZones.map(z => z.id)), [activeZones]);

  const dateSlotsRaw = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = formatDateLocal(selectedDate);
    const slotsForDate = getStaffSlotsForDate(selectedDate, allTimeSlots, dateOverrides, zoneWindows, activeZoneIds);
    
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const filtered = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate?.(ts.id, dateStr)) return false;
          return true;
        });

    return filtered.map(ts => ({
      id: ts.id,
      start: ts.start.substring(0, 5),
      end: ts.end.substring(0, 5),
      label: `${ts.start.substring(0, 5)} - ${ts.end.substring(0, 5)}`,
    }));
  }, [allTimeSlots, dateOverrides, selectedDate, isSlotClosedOnDate, zoneWindows, activeZoneIds]);

  const availableBookingTimes = useMemo(() => {
    if (!isTimeMode || !selectedDate || dateSlotsRaw.length === 0) return [];
    const intervalSetting = reservationSettings?.bookingIntervalMinutes;
    const interval = [15, 30, 60].includes(intervalSetting as number) ? intervalSetting as 15 | 30 | 60 : 30;
    const slotRanges = dateSlotsRaw.map(ts => ({ start: ts.start, end: ts.end }));
    return generateAvailableTimes(slotRanges, interval, effectiveDuration);
  }, [isTimeMode, selectedDate, dateSlotsRaw, reservationSettings, effectiveDuration]);

  const timeAvailability = useMemo(() => {
    if (!isTimeMode || !selectedDate || availableBookingTimes.length === 0) return [];
    const now = new Date();
    const dateStr = formatDateLocal(selectedDate);

    return availableBookingTimes.map(time => {
      if (isToday(selectedDate)) {
        const [hours, minutes] = time.split(':').map(Number);
        const bookingTime = new Date(selectedDate);
        bookingTime.setHours(hours, minutes, 0, 0);
        if (isBefore(bookingTime, now)) {
          return { time, isAvailable: false, unavailableReason: 'hours' as const };
        }
      }

      const blockedIds = getBlockedTableIds(reservations, combinedTables, selectedDate, time, { bookingMode, reservationDurationMinutes: effectiveDuration });
      const slotId = findParentSlotId(time, allTimeSlots);
      const terraceClosedForSlot = isTerraceClosedOnDate?.(dateStr, slotId) ?? false;

      const hasAvailSingle = activeTables.some(table => {
        if (blockedIds.has(table.id)) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return false;
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        if (!zoneAllows(table)) return false;
        return true;
      });

      const hasAvailCombined = activeCombined.some(ct => {
        if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        if (hasChildren && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        if (terraceClosedForSlot && ct.isTerrace) return false;
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;
        if (!zoneAllows(ct)) return false;
        return ct.tableIds.every(tid => {
          const t = tables.find(tb => tb.id === tid);
          if (!t) return false;
          const wa = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
        });
      });

      // Manager: also check unbookable/manual-only tables for physical availability
      const hasAvailManual = unbookableTables.some(table => {
        if (blockedIds.has(table.id)) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return false;
        if (hasChildren && table.allowsChildren === false) return false;
        if (strollerActive && table.allowsStroller === false) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        if (!zoneAllows(table)) return false;
        return true;
      });

      // Manager: also check unbookable combined tables for physical availability
      const hasAvailManualCombined = unbookableCombined.some(ct => {
        if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        if (hasChildren && ct.allowsChildren === false) return false;
        if (strollerActive && ct.allowsStroller === false) return false;
        if (terraceClosedForSlot && ct.isTerrace) return false;
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;
        if (!zoneAllows(ct)) return false;
        return ct.tableIds.every(tid => {
          const t = tables.find(tb => tb.id === tid);
          if (!t) return false;
          const wa = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
        });
      });

      const anyAvail = hasAvailSingle || hasAvailCombined || hasAvailManual || hasAvailManualCombined;
      return { time, isAvailable: anyAvail, unavailableReason: anyAvail ? undefined : 'full' as const };
    });
  }, [isTimeMode, selectedDate, availableBookingTimes, activeTables, unbookableTables, activeCombined, unbookableCombined, reservations, combinedTables, availabilities, weekdayAvailabilities, bookingMode, effectiveDuration, hasChildren, strollerActive, isTerraceClosedOnDate, allTimeSlots, terracePreference]);

  const slotAvailability = useMemo(() => {
    if (isTimeMode || !selectedDate || dateSlotsRaw.length === 0) return {};
    const dateStr = formatDateLocal(selectedDate);
    const result: Record<string, { available: number; total: number }> = {};

    dateSlotsRaw.forEach(slot => {
      const blockedIds = getBlockedTableIds(reservations, combinedTables, selectedDate, slot.start, { bookingMode, reservationDurationMinutes: effectiveDuration });
      const slotId = findParentSlotId(slot.start, allTimeSlots);
      const terraceClosedForSlot = isTerraceClosedOnDate?.(dateStr, slotId) ?? false;

      const availCount = activeTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        if (!zoneAllows(table)) return false;
        return true;
      }).length;

      const availCombinedCount = activeCombined.filter(ct => {
        if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        if (terraceClosedForSlot && ct.isTerrace) return false;
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;
        if (!zoneAllows(ct)) return false;
        return ct.tableIds.every(tid => {
          const t = tables.find(tb => tb.id === tid);
          if (!t) return false;
          const wa = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
        });
      }).length;

      // Manager: count unbookable/manual-only tables that are physically free
      const availManualCount = unbookableTables.filter(table => {
        if (blockedIds.has(table.id)) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
        if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return false;
        if (terraceClosedForSlot && table.isTerrace) return false;
        if (terracePreference === 'terrace' && !table.isTerrace) return false;
        if (terracePreference === 'indoor' && table.isTerrace) return false;
        if (!zoneAllows(table)) return false;
        return true;
      }).length;

      // Manager: count unbookable combined tables that are physically free
      const availManualCombinedCount = unbookableCombined.filter(ct => {
        if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return false;
        if (terraceClosedForSlot && ct.isTerrace) return false;
        if (terracePreference === 'terrace' && !ct.isTerrace) return false;
        if (terracePreference === 'indoor' && ct.isTerrace) return false;
        if (!zoneAllows(ct)) return false;
        return ct.tableIds.every(tid => {
          const t = tables.find(tb => tb.id === tid);
          if (!t) return false;
          const wa = weekdayAvailabilities.find(w => w.tableId === tid);
          return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
        });
      }).length;

      result[slot.start] = { available: availCount + availCombinedCount + availManualCount + availManualCombinedCount, total: activeTables.length + activeCombined.length + unbookableTables.length + unbookableCombined.length };
    });

    return result;
  }, [isTimeMode, selectedDate, dateSlotsRaw, activeTables, unbookableTables, activeCombined, unbookableCombined, reservations, combinedTables, availabilities, weekdayAvailabilities, bookingMode, effectiveDuration, isTerraceClosedOnDate, allTimeSlots, terracePreference]);

  // Detailed results for step 2
  const searchResults = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot) {
      return { available: [] as any[], unavailable: [] as any[], manualOnly: [] as any[] };
    }

    const adultsNum = childrenEnabled ? Math.max(1, guestCount - numberOfChildren) : guestCount;
    const effectiveCapacity = hasChildren
      ? calculateEffectiveCapacity(adultsNum, numberOfChildren, childrenCapacityMode)
      : guestCount;

    const blockedIds = getBlockedTableIds(reservations, combinedTables, selectedDate, selectedTimeSlot, { bookingMode, reservationDurationMinutes: effectiveDuration });
    const dateStr = formatDateLocal(selectedDate);
    const slotId = findParentSlotId(selectedTimeSlot, allTimeSlots);
    const terraceClosedForSlot = isTerraceClosedOnDate?.(dateStr, slotId) ?? false;

    const available: { id: string; name: string; capacity: number; type: "table" | "combined"; underCapacity?: boolean }[] = [];
    const unavailable: { id: string; name: string; capacity: number; reason: string; type: "table" | "combined" }[] = [];
    const manualOnly: { id: string; name: string; capacity: number; type: "table" | "combined"; reason: string | null }[] = [];

    activeTables.forEach(table => {
      const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
      const isAvailOnDate = isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);

      if (!isAvailOnDate) {
        unavailable.push({ id: table.id, name: table.name, capacity: table.capacity, reason: "Non disponible à cette date", type: "table" });
        return;
      }
      if (!isTableBookableOnDate(table, selectedDate, bookablePeriods)) {
        unavailable.push({ id: table.id, name: table.name, capacity: table.capacity, reason: "Temporairement non réservable", type: "table" });
        return;
      }
      if (blockedIds.has(table.id)) {
        unavailable.push({ id: table.id, name: table.name, capacity: table.capacity, reason: "Déjà réservée", type: "table" });
        return;
      }

      const reasons: string[] = [];
      if (terraceClosedForSlot && table.isTerrace) reasons.push("Terrasse fermée");
      if (terracePreference === 'terrace' && !table.isTerrace) reasons.push("Table en intérieur");
      if (terracePreference === 'indoor' && table.isTerrace) reasons.push("Table en terrasse");
      if (zonesUiEnabled && zonePreference.length > 0 && !zoneAllows(table)) reasons.push("Hors zone sélectionnée");
      if (hasChildren && table.allowsChildren === false) reasons.push("N'accepte pas les enfants");
      if (strollerActive && table.allowsStroller === false) reasons.push("N'accepte pas les poussettes");
      const isUnderCapacity = table.capacity < effectiveCapacity;
      if (isUnderCapacity) reasons.push("Capacité insuffisante");

      if (reasons.length > 0) {
        if (includeUnderCapacity && reasons.length === 1 && isUnderCapacity) {
          available.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table", underCapacity: true });
          return;
        }
        unavailable.push({ id: table.id, name: table.name, capacity: table.capacity, reason: reasons.join(" · "), type: "table" });
        return;
      }
      available.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table" });
    });

    activeCombined.forEach(ct => {
      if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) {
        unavailable.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, reason: "Déjà réservée", type: "combined" });
        return;
      }
      const allSubAvail = ct.tableIds.every(tid => {
        const t = tables.find(tb => tb.id === tid);
        if (!t) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
      });
      if (!allSubAvail) {
        unavailable.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, reason: "Sous-table indisponible", type: "combined" });
        return;
      }

      const noTempBlocking = ct.tableIds.every(tid => {
        return !isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods);
      });
      if (!noTempBlocking) {
        unavailable.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, reason: "Sous-table temporairement non réservable", type: "combined" });
        return;
      }

      const reasons: string[] = [];
      if (terraceClosedForSlot && ct.isTerrace) reasons.push("Terrasse fermée");
      if (terracePreference === 'terrace' && !ct.isTerrace) reasons.push("Table en intérieur");
      if (terracePreference === 'indoor' && ct.isTerrace) reasons.push("Table en terrasse");
      if (zonesUiEnabled && zonePreference.length > 0 && !zoneAllows(ct)) reasons.push("Hors zone sélectionnée");
      if (hasChildren && ct.allowsChildren === false) reasons.push("N'accepte pas les enfants");
      if (strollerActive && ct.allowsStroller === false) reasons.push("N'accepte pas les poussettes");
      if (hasChildren) {
        const allAllow = ct.tableIds.every(tid => { const t = tables.find(tb => tb.id === tid); return t && t.allowsChildren !== false; });
        if (!allAllow && !reasons.some(r => r.includes("enfants"))) reasons.push("Sous-table n'accepte pas les enfants");
      }
      if (strollerActive && ct.strollerPolicy !== 'allow') {
        const allAllow = ct.tableIds.every(tid => { const t = tables.find(tb => tb.id === tid); return t && t.allowsStroller !== false; });
        if (!allAllow && !reasons.some(r => r.includes("poussettes"))) reasons.push("Sous-table n'accepte pas les poussettes");
      }
      const isUnderCapacity = ct.totalCapacity < effectiveCapacity;
      if (isUnderCapacity) reasons.push("Capacité insuffisante");

      if (reasons.length > 0) {
        if (includeUnderCapacity && reasons.length === 1 && isUnderCapacity) {
          available.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, type: "combined", underCapacity: true });
          return;
        }
        unavailable.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, reason: reasons.join(" · "), type: "combined" });
        return;
      }
      available.push({ id: ct.id, name: ct.name, capacity: ct.totalCapacity, type: "combined" });
    });

    unbookableTables.forEach(table => {
      const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
      const isAvailOnDate = isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
      if (!isAvailOnDate) {
        manualOnly.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table", reason: "Non disponible" });
        return;
      }
      if (blockedIds.has(table.id)) {
        manualOnly.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table", reason: "Déjà réservée" });
        return;
      }
      if (terraceClosedForSlot && table.isTerrace) {
        manualOnly.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table", reason: "Terrasse fermée" });
        return;
      }
      manualOnly.push({ id: table.id, name: table.name, capacity: table.capacity, type: "table", reason: null });
    });

    // Combinées hors réservation : visibles avec assignation manuelle staff
    unbookableCombined.forEach(ct => {
      const reasons: string[] = [];

      // Sous-tables introuvables
      const missingSub = ct.tableIds.some(tid => !tables.find(t => t.id === tid));
      if (missingSub) reasons.push("Sous-table(s) introuvable(s)");

      // Indisponibilité d'une sous-table à la date
      const subUnavailable = ct.tableIds.some(tid => {
        const t = tables.find(tb => tb.id === tid);
        if (!t) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === tid);
        return !isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
      });
      if (subUnavailable) reasons.push("Sous-table indisponible");

      // Blocage temporaire d'une sous-table
      const subTempBlocked = ct.tableIds.some(tid =>
        isTableInTemporaryBlockingPeriod(tid, selectedDate, bookablePeriods)
      );
      if (subTempBlocked) reasons.push("Sous-table temporairement non réservable");

      // Conflit de réservation (combinée elle-même ou sous-table)
      if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) {
        reasons.push("Déjà réservée");
      }

      if (terraceClosedForSlot && ct.isTerrace) reasons.push("Terrasse fermée");
      if (terracePreference === 'terrace' && !ct.isTerrace) reasons.push("Table en intérieur");
      if (terracePreference === 'indoor' && ct.isTerrace) reasons.push("Table en terrasse");
      if (zonesUiEnabled && zonePreference.length > 0 && !zoneAllows(ct)) reasons.push("Hors zone sélectionnée");

      manualOnly.push({
        id: ct.id,
        name: ct.name,
        capacity: ct.totalCapacity,
        type: "combined",
        reason: reasons.length > 0 ? reasons.join(" · ") : null,
      });
    });

    const byCap = <T extends { capacity: number }>(a: T, b: T) => a.capacity - b.capacity;
    const sortedAvailable = sortByCustomOrder(available, customTableOrder, byCap);
    const sortedUnavailable = sortByCustomOrder(unavailable, customTableOrder, byCap);
    const sortedManualOnly = sortByCustomOrder(manualOnly, customTableOrder, byCap);

    return { available: sortedAvailable, unavailable: sortedUnavailable, manualOnly: sortedManualOnly };
  }, [selectedDate, selectedTimeSlot, guestCount, numberOfChildren, childrenEnabled, childrenCapacityMode, hasChildren, strollerActive, tables, activeTables, unbookableTables, activeCombined, unbookableCombined, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, bookingMode, effectiveDuration, isTerraceClosedOnDate, allTimeSlots, terracePreference, includeUnderCapacity, zonesUiEnabled, zonePreference, customTableOrder]);

  const dateClosed = isDateClosed(selectedDate);

  const liveAvailableCount = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || dateClosed) return null;

    const adultsNum = childrenEnabled ? Math.max(1, guestCount - numberOfChildren) : guestCount;
    const effectiveCapacity = hasChildren
      ? calculateEffectiveCapacity(adultsNum, numberOfChildren, childrenCapacityMode)
      : guestCount;

    const blockedIds = getBlockedTableIds(reservations, combinedTables, selectedDate, selectedTimeSlot, { bookingMode, reservationDurationMinutes: effectiveDuration });
    const dateStr = formatDateLocal(selectedDate);
    const slotId = findParentSlotId(selectedTimeSlot, allTimeSlots);
    const terraceClosedForSlot = isTerraceClosedOnDate?.(dateStr, slotId) ?? false;

    let simpleCount = 0;
    let combinedCount = 0;

    activeTables.forEach(table => {
      const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
      if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return;
      if (!isTableBookableOnDate(table, selectedDate, bookablePeriods)) return;
      if (blockedIds.has(table.id)) return;
      if (terraceClosedForSlot && table.isTerrace) return;
      if (terracePreference === 'terrace' && !table.isTerrace) return;
      if (terracePreference === 'indoor' && table.isTerrace) return;
      if (!zoneAllows(table)) return;
      if (hasChildren && table.allowsChildren === false) return;
      if (strollerActive && table.allowsStroller === false) return;
      if (table.capacity < effectiveCapacity) return;
      simpleCount++;
    });

    activeCombined.forEach(ct => {
      if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return;
      const allSubAvail = ct.tableIds.every(tid => {
        const t = tables.find(tb => tb.id === tid);
        if (!t) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
      });
      if (!allSubAvail) return;
      const noTempBlocking = ct.tableIds.every(tid => {
        const sub = tables.find(t => t.id === tid);
        return sub ? isTableBookableOnDate(sub, selectedDate, bookablePeriods) : false;
      });
      if (!noTempBlocking) return;
      if (terraceClosedForSlot && ct.isTerrace) return;
      if (terracePreference === 'terrace' && !ct.isTerrace) return;
      if (terracePreference === 'indoor' && ct.isTerrace) return;
      if (!zoneAllows(ct)) return;
      if (hasChildren && ct.allowsChildren === false) return;
      if (strollerActive && ct.allowsStroller === false) return;
      if (hasChildren) {
        const allAllow = ct.tableIds.every(tid => { const t = tables.find(tb => tb.id === tid); return t && t.allowsChildren !== false; });
        if (!allAllow) return;
      }
      if (strollerActive && ct.strollerPolicy !== 'allow') {
        const allAllow = ct.tableIds.every(tid => { const t = tables.find(tb => tb.id === tid); return t && t.allowsStroller !== false; });
        if (!allAllow) return;
      }
      if (ct.totalCapacity < effectiveCapacity) return;
      combinedCount++;
    });

    // Count unbookable/manual-only tables that are physically free
    let manualCount = 0;
    unbookableTables.forEach(table => {
      const wa = weekdayAvailabilities.find(w => w.tableId === table.id);
      if (!isTableAvailableOnDate(table, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null)) return;
      if (blockedIds.has(table.id)) return;
      if (terraceClosedForSlot && table.isTerrace) return;
      if (terracePreference === 'terrace' && !table.isTerrace) return;
      if (terracePreference === 'indoor' && table.isTerrace) return;
      if (!zoneAllows(table)) return;
      if (hasChildren && table.allowsChildren === false) return;
      if (strollerActive && table.allowsStroller === false) return;
      if (table.capacity < effectiveCapacity) return;
      manualCount++;
    });

    // Count unbookable combined tables that are physically free
    unbookableCombined.forEach(ct => {
      if (blockedIds.has(ct.id) || ct.tableIds.some(tid => blockedIds.has(tid))) return;
      const allSubAvail = ct.tableIds.every(tid => {
        const t = tables.find(tb => tb.id === tid);
        if (!t) return false;
        const wa = weekdayAvailabilities.find(w => w.tableId === tid);
        return isTableAvailableOnDate(t, selectedDate, availabilities.filter(a => !a.isAvailable), wa || null);
      });
      if (!allSubAvail) return;
      if (terraceClosedForSlot && ct.isTerrace) return;
      if (terracePreference === 'terrace' && !ct.isTerrace) return;
      if (terracePreference === 'indoor' && ct.isTerrace) return;
      if (!zoneAllows(ct)) return;
      if (hasChildren && ct.allowsChildren === false) return;
      if (strollerActive && ct.allowsStroller === false) return;
      if (ct.totalCapacity < effectiveCapacity) return;
      manualCount++;
    });

    return { simple: simpleCount, combined: combinedCount, total: simpleCount + combinedCount + manualCount };
  }, [selectedDate, selectedTimeSlot, dateClosed, guestCount, numberOfChildren, childrenEnabled, childrenCapacityMode, hasChildren, strollerActive, activeTables, unbookableTables, activeCombined, unbookableCombined, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, bookingMode, effectiveDuration, isTerraceClosedOnDate, allTimeSlots, terracePreference]);

  // Optimal table suggestion for quick-book
  const optimalSuggestion = useMemo(() => {
    if (!selectedDate || !selectedTimeSlot || dateClosed) return null;

    const adultsNum = childrenEnabled ? Math.max(1, guestCount - numberOfChildren) : guestCount;

    return findOptimalTable(
      adultsNum,
      selectedDate,
      selectedTimeSlot,
      tables,
      combinedTables,
      reservations,
      availabilities,
      weekdayAvailabilities,
      { bookingMode, reservationDurationMinutes: effectiveDuration },
      bookablePeriods,
      numberOfChildren,
      childrenCapacityMode,
      hasStroller && strollerEnabled,
      terracePreference,
      isTerraceClosedOnDate,
      customAssignmentOrderEnabled,
      zonesUiEnabled ? zonePreference : []
    );
  }, [selectedDate, selectedTimeSlot, dateClosed, guestCount, numberOfChildren, childrenEnabled, childrenCapacityMode, hasStroller, strollerEnabled, tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods, bookingMode, effectiveDuration, terracePreference, isTerraceClosedOnDate, customAssignmentOrderEnabled, zonesUiEnabled, zonePreference]);

  const handleViewResults = () => {
    if (selectedTimeSlot) setStep("results");
  };

  const handleQuickBook = () => {
    if (!optimalSuggestion) return;
    handleTableSelect({
      id: optimalSuggestion.id,
      name: optimalSuggestion.name,
      capacity: optimalSuggestion.capacity,
      type: optimalSuggestion.type === 'single' ? 'table' : 'combined',
    });
  };

  const handleTableSelect = (item: { id: string; name: string; capacity: number; type: "table" | "combined" }) => {
    if (!onReservationCreate) return;
    setSelectedTableForBooking({ id: item.id, type: item.type, name: item.name, capacity: item.capacity });
    // Reset only notes/source, keep client selection from search step
    setNotes("");
    setInternalNotes("");
    setSource("");
    setEditingCustomerNotes(false);
    setCustomerNotesValue("");
    setStep("booking");
  };

  // Client search (WalkIn-style autocomplete)
  const filteredClients = useMemo(() => {
    if (!freeTextName.trim() || selectedClient) return [];
    const query = freeTextName.toLowerCase();
    const queryNorm = normalizePhoneForSearch(query);
    return existingClients
      .filter((c) => {
        const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
        const email = c.email?.toLowerCase() || "";
        const phoneNorm = normalizePhoneForSearch(c.phone || '');
        return fullName.includes(query) || email.includes(query) ||
          (queryNorm.length >= 2 && phoneNorm.includes(queryNorm));
      })
      .slice(0, 6);
  }, [freeTextName, existingClients, selectedClient]);

  // Build customer payload from client selection or free text
  const getCustomerPayload = () => {
    if (selectedClient) {
      return {
        customerFirstName: selectedClient.firstName,
        customerLastName: selectedClient.lastName,
        customerEmail: selectedClient.email || undefined,
        customerPhone: selectedClient.phone || undefined,
      };
    }
    if (freeTextName.trim()) {
      const { firstName, lastName } = parseFreeName(freeTextName);
      return { customerFirstName: firstName, customerLastName: lastName };
    }
    return { customerFirstName: "", customerLastName: "" };
  };

  const handleBookingSubmit = async () => {
    if (!onReservationCreate || !selectedTableForBooking || isSubmitting) return;

    const customer = getCustomerPayload();
    if (!customer.customerFirstName && !customer.customerLastName) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez saisir au moins un prénom ou un nom",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await onReservationCreate({
        ...customer,
        numberOfPeople: childrenEnabled ? guestCount - numberOfChildren : guestCount,
        numberOfChildren: childrenEnabled ? numberOfChildren : undefined,
        hasStroller: strollerEnabled ? hasStroller : undefined,
        terracePreference: terraceEnabled ? terracePreference : undefined,
        tableId: selectedTableForBooking.type === "table" ? selectedTableForBooking.id : undefined,
        combinedTableId: selectedTableForBooking.type === "combined" ? selectedTableForBooking.id : undefined,
        startTime: selectedTimeSlot,
        date: selectedDate,
        notes: notes || undefined,
        internalNotes: internalNotes || undefined,
        durationMinutes: customDuration || undefined,
        source: source || undefined,
        zonePreference: zonesUiEnabled && zonePreference.length > 0 ? zonePreference : undefined,
      });

      handleOpenChange(false);
    } catch {
      // Error handled by parent (toast in handleCreateReservation)
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => addDays(today, i));
  }, []);

  const isQuickDate = quickDates.some(d => isSameDay(d, selectedDate));
  const formattedDate = selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md max-h-[92vh] sm:max-h-[85vh] min-h-0 flex flex-col overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-1 sm:space-y-1.5">
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {step === "booking" ? "Nouvelle réservation" : "Disponibilités"}
          </DialogTitle>
          <DialogDescription>
            {step === "search"
              ? `${formattedDate} — Recherche rapide`
              : step === "results"
              ? `${formattedDate} — ${guestCount} pers.${hasChildren ? ` (dont ${numberOfChildren} enf.)` : ''} · ${selectedTimeSlot}`
              : `${formattedDate} — ${selectedTableForBooking?.name}`}
          </DialogDescription>
        </DialogHeader>

        {step === "search" && (
          <>
          <div className="space-y-3 sm:space-y-5 overflow-y-auto flex-1 min-h-0 pr-1">
            {/* Date selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Date</label>
              <div className="flex flex-wrap gap-1.5">
                {quickDates.map((date, i) => {
                  const closed = isDateClosed(date);
                  return (
                    <Button
                      key={i}
                      variant={isSameDay(selectedDate, date) ? "default" : "outline"}
                      className={cn("flex flex-col items-center h-auto py-1 sm:py-1.5 px-2.5 min-w-[2.8rem]", closed && "opacity-50")}
                      onClick={() => setSelectedDate(date)}
                    >
                      <span className="text-[10px] uppercase leading-tight">
                        {i === 0 ? "Auj." : format(date, "EEE", { locale: fr })}
                      </span>
                      <span className="text-sm font-semibold">{format(date, "d")}</span>
                    </Button>
                  );
                })}
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button variant={!isQuickDate ? "default" : "outline"} className="flex items-center gap-1 h-auto py-1 sm:py-1.5 px-2.5">
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => { if (date) setSelectedDate(date); setIsCalendarOpen(false); }}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      locale={fr}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {dateClosed && (
                <p className="text-xs text-destructive font-medium">⚠️ Établissement fermé ce jour</p>
              )}
            </div>

            {/* Terrace preference */}
            {terraceEnabled && (
              <TerracePreferenceSelector value={terracePreference} onChange={setTerracePreference} compact />
            )}

            {/* Zone preference */}
            {zonesUiEnabled && (
              <ZonePreferenceSelector
                zones={activeZones.map(z => ({ id: z.id, name: z.name, color: z.color }))}
                value={zonePreference}
                onChange={setZonePreference}
                mode="multi"
                compact
              />
            )}

            {/* Guest count grid */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Nombre de personnes</label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <Button
                    key={n}
                    variant={guestCount === n ? "default" : "outline"}
                    className={cn("h-10 sm:h-12 text-base sm:text-lg font-bold", guestCount === n && "ring-2 ring-primary ring-offset-2")}
                    onClick={() => setGuestCount(n)}
                  >
                    {n}
                  </Button>
                ))}
                <Button
                  variant={guestCount > 7 ? "default" : "outline"}
                  className={cn("h-10 sm:h-12 text-base sm:text-lg font-bold", guestCount > 7 && "ring-2 ring-primary ring-offset-2")}
                  onClick={() => setGuestCount(guestCount > 7 ? guestCount : 8)}
                  aria-label={guestCount > 7 ? `${guestCount} personnes` : "Plus de personnes"}
                >
                  {guestCount > 7 ? guestCount : <Plus className="w-5 h-5" />}
                </Button>
              </div>
              {guestCount > 7 && (
                <div className="mt-2 flex items-center justify-between gap-2 h-11 rounded-md border border-primary/30 bg-primary/10 px-2">
                  <Button
                    type="button"
                    variant="default"
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full"
                    onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                    aria-label="Diminuer le nombre de personnes"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-semibold tabular-nums">
                    <span className="text-lg">{guestCount}</span> personnes
                  </span>
                  <Button
                    type="button"
                    variant="default"
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full"
                    onClick={() => setGuestCount(Math.min(50, guestCount + 1))}
                    aria-label="Augmenter le nombre de personnes"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Children selector */}
            {childrenEnabled && guestCount > 1 && (
              <ChildrenSelector
                value={numberOfChildren}
                onChange={setNumberOfChildren}
                max={guestCount - 1}
              />
            )}

            {/* Stroller checkbox */}
            {strollerEnabled && hasChildren && (
              <div className="flex items-center gap-2">
                <Checkbox id="stroller" checked={hasStroller} onCheckedChange={(v) => setHasStroller(!!v)} />
                <label htmlFor="stroller" className="text-sm cursor-pointer">Poussette</label>
              </div>
            )}

            {/* Time slot / time selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {isTimeMode ? "Heure de réservation" : "Créneau horaire"}
              </label>

              {dateClosed ? (
                <p className="text-sm text-muted-foreground">Établissement fermé ce jour.</p>
              ) : isTimeMode ? (
                availableBookingTimes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun horaire disponible pour ce jour.</p>
                ) : (
                  <TimeBookingGrid
                    availableTimes={timeAvailability}
                    selectedTime={selectedTimeSlot}
                    onTimeSelect={setSelectedTimeSlot}
                  />
                )
              ) : dateSlotsRaw.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun créneau configuré pour ce jour.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {dateSlotsRaw.map((slot) => {
                    const info = slotAvailability[slot.start];
                    const isSelected = selectedTimeSlot === slot.start;
                    const hasAvail = info && info.available > 0;

                    return (
                      <Button
                        key={slot.start}
                        variant={isSelected ? "default" : "outline"}
                        className={cn(
                          "h-auto py-2 sm:py-2.5 px-3 flex flex-col items-start gap-0.5",
                          isSelected && "ring-2 ring-primary ring-offset-2",
                          !hasAvail && "opacity-50"
                        )}
                        onClick={() => setSelectedTimeSlot(slot.start)}
                      >
                        <span className="text-sm font-semibold">{slot.label}</span>
                        {info && (
                          <span className="text-[10px] text-muted-foreground">
                            {info.available} table{info.available > 1 ? "s" : ""} dispo
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Client identification field (WalkIn-style) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                Client
              </label>
              <div className="relative">
                {selectedClient ? (
                  <div className="flex items-center h-10 rounded-md border border-input bg-muted/30 px-3 gap-2">
                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 text-sm font-medium truncate">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </span>
                    {selectedClient.email && (
                      <span className="text-xs text-muted-foreground truncate">{selectedClient.email}</span>
                    )}
                    {selectedClient.guestStatus === 'vip' && (
                      <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                    )}
                    {selectedClient.isBlacklisted && (
                      <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">Blacklist</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => {
                        setSelectedClient(null);
                        setFreeTextName("");
                        setTimeout(() => clientInputRef.current?.focus(), 50);
                      }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative flex gap-2">
                    <div className="relative flex-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        ref={clientInputRef}
                        type="text"
                        value={freeTextName}
                        onChange={(e) => {
                          setFreeTextName(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => freeTextName.trim() && setShowClientDropdown(true)}
                        placeholder="Nom ou téléphone"
                        className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      onClick={() => setShowCreateClientDialog(true)}
                      title="Créer un nouveau client"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Autocomplete dropdown */}
                {showClientDropdown && !selectedClient && freeTextName.trim() && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-md max-h-48 overflow-y-auto"
                  >
                    {filteredClients.map((client) => (
                      <button
                        key={client.id || `${client.firstName}-${client.lastName}`}
                        className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors flex items-center gap-2 text-sm"
                        onClick={() => {
                          setSelectedClient(client);
                          setFreeTextName(`${client.firstName} ${client.lastName}`);
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">
                            {client.firstName} {client.lastName}
                          </span>
                          {client.phone && (
                            <span className="text-muted-foreground ml-2 text-xs">
                              📞 {client.phone}
                            </span>
                          )}
                        </div>
                        {client.guestStatus === 'vip' && (
                          <Badge variant="default" className="text-[10px] px-1 py-0 shrink-0">VIP</Badge>
                        )}
                        {client.isBlacklisted && (
                          <Badge variant="destructive" className="text-[10px] px-1 py-0 shrink-0">⚠</Badge>
                        )}
                      </button>
                    ))}
                    {/* Option to use free text name */}
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-accent/50 transition-colors text-sm border-t border-border text-muted-foreground"
                      onClick={() => setShowClientDropdown(false)}
                    >
                      Utiliser « {freeTextName.trim()} »
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Duration selector (TIME mode only) + under-capacity toggle */}
            <div className="space-y-3">
              {isTimeMode && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Durée de la réservation</label>
                  <Select
                    value={customDuration !== null ? customDuration.toString() : "default"}
                    onValueChange={(v) => setCustomDuration(v === "default" ? null : parseInt(v))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Par défaut ({reservationDurationMinutes} min)</SelectItem>
                      <SelectItem value="60">1h</SelectItem>
                      <SelectItem value="90">1h30</SelectItem>
                      <SelectItem value="120">2h</SelectItem>
                      <SelectItem value="150">2h30</SelectItem>
                      <SelectItem value="180">3h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center justify-between">
                <label htmlFor="under-capacity" className="text-xs text-muted-foreground cursor-pointer">
                  Inclure les tables en sous-capacité
                </label>
                <Switch
                  id="under-capacity"
                  checked={includeUnderCapacity}
                  onCheckedChange={setIncludeUnderCapacity}
                />
              </div>
            </div>
          </div>

          {/* Sticky footer with integrated counter + button */}
          <div className="border-t pt-3 mt-auto shrink-0">
            {liveAvailableCount !== null && (
              <div className={cn(
                "flex items-center gap-2 rounded-lg border p-2.5 text-sm mb-3",
                liveAvailableCount.total > 0
                  ? "border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400"
                  : "border-destructive/30 bg-destructive/10 text-destructive"
              )}>
                {liveAvailableCount.total > 0 ? (
                  <CheckCircle className="w-4 h-4 shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 shrink-0" />
                )}
                <div>
                  <span className="font-semibold">
                    {liveAvailableCount.total > 0
                      ? `${liveAvailableCount.total} table${liveAvailableCount.total > 1 ? 's' : ''} disponible${liveAvailableCount.total > 1 ? 's' : ''}`
                      : 'Aucune table disponible'}
                  </span>
                  {liveAvailableCount.total > 0 && (liveAvailableCount.simple > 0 || liveAvailableCount.combined > 0) && (
                    <span className="text-xs opacity-75 ml-1">
                      ({liveAvailableCount.simple > 0 ? `${liveAvailableCount.simple} simple${liveAvailableCount.simple > 1 ? 's' : ''}` : ''}
                      {liveAvailableCount.simple > 0 && liveAvailableCount.combined > 0 ? ' · ' : ''}
                      {liveAvailableCount.combined > 0 ? `${liveAvailableCount.combined} combinée${liveAvailableCount.combined > 1 ? 's' : ''}` : ''})
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className={cn("flex gap-2", optimalSuggestion && selectedTimeSlot ? "flex-col sm:flex-row" : "")}>
              <Button
                className="flex-1"
                variant="outline"
                disabled={!selectedTimeSlot || dateClosed}
                onClick={handleViewResults}
              >
                <Users className="w-4 h-4 mr-2" />
                Voir les tables
              </Button>
              {optimalSuggestion && selectedTimeSlot && !dateClosed && (
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleQuickBook}
                >
                  <Armchair className="w-4 h-4 mr-2" />
                  Réserver · {optimalSuggestion.name}
                </Button>
              )}
            </div>
          </div>
        </>
        )}

        {step === "results" && (
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <Button variant="ghost" size="sm" onClick={() => setStep("search")} className="mb-1">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Modifier la recherche
            </Button>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="rounded-md border p-3">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{searchResults.available.length}</div>
                <div className="text-xs text-muted-foreground">Disponible{searchResults.available.length > 1 ? "s" : ""}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{searchResults.unavailable.length}</div>
                <div className="text-xs text-muted-foreground">Indisponible{searchResults.unavailable.length > 1 ? "s" : ""}</div>
              </div>
            </div>

            {/* Available tables */}
            {searchResults.available.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  Tables disponibles
                </h4>
                {searchResults.available.map((item: any) => (
                  <Card
                    key={item.id}
                    className={cn("cursor-pointer hover:shadow-md transition-all", onReservationCreate && "active:scale-[0.98]")}
                    onClick={() => handleTableSelect(item)}
                  >
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Armchair className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{item.name}</span>
                        {item.type === "combined" && <Badge variant="outline" className="text-[10px]">Combinée</Badge>}
                        {item.underCapacity && (
                          <Badge variant="outline" className="text-[10px] border-orange-500/50 text-orange-600 dark:text-orange-400">
                            <AlertTriangle className="w-3 h-3 mr-0.5" />
                            Sous-capacité
                          </Badge>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">{item.capacity} pers.</Badge>
                    </CardContent>
                  </Card>
                ))}
                {onReservationCreate && (
                  <p className="text-[10px] text-muted-foreground text-center mt-1">Cliquez sur une table pour créer une réservation</p>
                )}
              </div>
            )}

            {/* Non-bookable tables (manual assignment) */}
            {searchResults.manualOnly.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
                  <ShieldAlert className="w-4 h-4" />
                  Tables hors réservation
                </h4>
                {searchResults.manualOnly.map((item: any) => {
                  const isFree = !item.reason;
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between rounded-md border border-dashed p-3 transition-all ${isFree && onReservationCreate ? 'cursor-pointer hover:shadow-md hover:border-primary/40 hover:bg-accent/50' : 'opacity-60'}`}
                      onClick={isFree && onReservationCreate ? () => handleTableSelect(item) : undefined}
                    >
                      <div className="flex items-center gap-2">
                        <Armchair className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{item.name}</span>
                        {item.type === "combined" && <Badge variant="outline" className="text-[10px]">Combinée</Badge>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{item.capacity} pers.</Badge>
                        {item.reason && <span className="text-xs text-muted-foreground">{item.reason}</span>}
                        {isFree && <Badge variant="outline" className="text-[10px] text-green-600">Libre — cliquez pour réserver</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unavailable tables */}
            {searchResults.unavailable.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5 text-muted-foreground">
                  <XCircle className="w-4 h-4" />
                  Tables indisponibles
                </h4>
                {searchResults.unavailable.map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between rounded-md border border-dashed p-3 opacity-60">
                    <div className="flex items-center gap-2">
                      <Armchair className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{item.name}</span>
                      {item.type === "combined" && <Badge variant="outline" className="text-[10px]">Combinée</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground max-w-[50%] text-right">{item.reason}</span>
                  </div>
                ))}
              </div>
            )}

            {searchResults.available.length === 0 && searchResults.unavailable.length === 0 && searchResults.manualOnly.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune table trouvée pour ces critères.</p>
            )}
          </div>
        )}

        {step === "booking" && selectedTableForBooking && (
          <>
            {/* Sticky recap header */}
            <div className="shrink-0 border-b pb-3 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-xs">
                  <CalendarIcon className="w-3 h-3 mr-1" />
                  {format(selectedDate, "EEE d MMM", { locale: fr })}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {selectedTimeSlot}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />
                  {guestCount} pers.{hasChildren ? ` (${numberOfChildren} enf.)` : ''}
                </Badge>
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer hover:bg-accent"
                  onClick={() => setStep("results")}
                >
                  <Armchair className="w-3 h-3 mr-1" />
                  {selectedTableForBooking.name} ({selectedTableForBooking.capacity}p)
                  {selectedTableForBooking.type === "combined" && " · Combinée"}
                </Badge>
                {isTimeMode && customDuration && (
                  <Badge variant="secondary" className="text-xs">{effectiveDuration} min</Badge>
                )}
                {strollerActive && (
                  <Badge variant="secondary" className="text-xs">🚼 Poussette</Badge>
                )}
                {terracePreference !== 'no_preference' && (
                  <Badge variant="secondary" className="text-xs">
                    {terracePreference === 'terrace' ? '☀️ Terrasse' : '🏠 Intérieur'}
                  </Badge>
                )}
                {zonesUiEnabled && zonePreference.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    📍 {zonePreference.length === 1
                      ? (activeZones.find(z => z.id === zonePreference[0])?.name ?? 'Zone')
                      : `${zonePreference.length} zones`}
                  </Badge>
                )}
              </div>
            </div>

            {/* Scrollable booking form (simplified - client already selected in search step) */}
            <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
              {/* Selected client recap */}
              {selectedClient && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <User className="w-4 h-4" />
                    <span className="font-medium text-sm">Client : {selectedClient.firstName} {selectedClient.lastName}</span>
                    {selectedClient.email && (
                      <span className="text-xs opacity-75">({selectedClient.email})</span>
                    )}
                  </div>
                </div>
              )}

              {!selectedClient && freeTextName.trim() && (
                <div className="p-3 bg-muted/50 border rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span className="text-sm">Nouveau client : <span className="font-medium text-foreground">{freeTextName.trim()}</span></span>
                  </div>
                </div>
              )}

              {!selectedClient && !freeTextName.trim() && (
                <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                    Aucun client sélectionné. Retournez à l'étape précédente pour identifier le client.
                  </AlertDescription>
                </Alert>
              )}

              {/* Alerts: blacklist, no-show, allergies */}
              {selectedClient && (() => {
                const emailToCheck = selectedClient.email;
                if (!emailToCheck) return null;
                const record = customerNotes.get(emailToCheck.toLowerCase());
                if (!record) return null;
                return (
                  <>
                    {record.isBlacklisted && (
                      <Alert className="border-destructive bg-destructive/10">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                        <AlertDescription className="text-destructive font-medium text-sm">
                          ⚠️ Ce client est blacklisté
                        </AlertDescription>
                      </Alert>
                    )}
                    {record.hasNoShow && (
                      <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700 dark:text-amber-400 font-medium text-sm">
                          Ce client a un historique de no-show
                        </AlertDescription>
                      </Alert>
                    )}
                    {record.allergiesTags && record.allergiesTags.length > 0 && (
                      <Alert className="border-orange-400 bg-orange-50 dark:bg-orange-950/20">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-700 dark:text-orange-400 text-sm">
                          <span className="font-medium">Allergies :</span> {record.allergiesTags.join(', ')}
                          {record.allergiesNotes && <p className="text-xs mt-1">{record.allergiesNotes}</p>}
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                );
              })()}

              {/* Customer persistent notes */}
              {selectedClient && (() => {
                const emailToCheck = selectedClient.email;
                const existingNotes = getNotesForEmail(emailToCheck);
                const customerId = getCustomerIdForEmail(emailToCheck);
                if (!emailToCheck || (!existingNotes && !editingCustomerNotes)) return null;
                return (
                  <div className="space-y-2 p-3 rounded-lg border-2 border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-950/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StickyNote className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        <Label className="text-violet-700 dark:text-violet-300 font-medium text-xs">Notes client (persistantes)</Label>
                      </div>
                      {!editingCustomerNotes && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-violet-600 dark:text-violet-400"
                          onClick={() => {
                            setEditingCustomerNotes(true);
                            setCustomerNotesValue(existingNotes || "");
                          }}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Modifier
                        </Button>
                      )}
                    </div>
                    {editingCustomerNotes ? (
                      <div className="space-y-2">
                        <Textarea
                          value={customerNotesValue}
                          onChange={(e) => setCustomerNotesValue(e.target.value)}
                          placeholder="Préférences, allergies, notes importantes..."
                          className="min-h-[60px] border-violet-300 dark:border-violet-700"
                          maxLength={1000}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setEditingCustomerNotes(false)}>
                            Annuler
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={savingCustomerNotes}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            onClick={async () => {
                              if (!customerId || !resolvedEstablishmentId) return;
                              setSavingCustomerNotes(true);
                              try {
                                const { error } = await supabase
                                  .from('customers')
                                  .update({ notes: customerNotesValue || null })
                                  .eq('id', customerId)
                                  .eq('establishment_id', resolvedEstablishmentId);
                                if (error) throw error;
                                await refetchCustomerNotes();
                                setEditingCustomerNotes(false);
                                toast({ title: "Notes client mises à jour" });
                              } catch {
                                toast({ title: "Erreur", description: "Impossible de sauvegarder les notes", variant: "destructive" });
                              } finally {
                                setSavingCustomerNotes(false);
                              }
                            }}
                          >
                            {savingCustomerNotes ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                            Enregistrer
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-violet-800 dark:text-violet-200 whitespace-pre-wrap">{existingNotes}</p>
                    )}
                  </div>
                );
              })()}

              {/* Reservation notes */}
              <div className="space-y-1.5">
                <Label htmlFor="avail-notes" className="text-xs">Note de réservation</Label>
                <Input
                  id="avail-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Demande spéciale..."
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">Note ajoutée à la réservation.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="avail-internalNotes" className="text-xs">Note interne</Label>
                <Textarea
                  id="avail-internalNotes"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Notes internes pour l'équipe..."
                  maxLength={1000}
                  className="min-h-[60px]"
                />
                <p className="text-xs text-muted-foreground">Visible uniquement par l'équipe.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="avail-source" className="text-xs">Source de la réservation</Label>
                <Select value={source || "none"} onValueChange={(val) => setSource(val === "none" ? "" : val)}>
                  <SelectTrigger id="avail-source" className="h-9">
                    <SelectValue placeholder="Sélectionner une source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non renseignée</SelectItem>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Site web">Site web</SelectItem>
                    <SelectItem value="Instagram">Instagram</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                    <SelectItem value="TheFork">TheFork</SelectItem>
                    <SelectItem value="Téléphone">Téléphone</SelectItem>
                    <SelectItem value="Passage">Passage</SelectItem>
                    <SelectItem value="Bouche à oreille">Bouche à oreille</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="border-t pt-3 mt-auto shrink-0 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep("results")} className="shrink-0">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <Button
                className="flex-1"
                disabled={isSubmitting || (!selectedClient && !freeTextName.trim())}
                onClick={handleBookingSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer la réservation'
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>

      <CreateClientDialog
        open={showCreateClientDialog}
        onOpenChange={setShowCreateClientDialog}
        initialName={freeTextName}
        onClientCreated={(client) => {
          setSelectedClient(client);
          setFreeTextName(`${client.firstName} ${client.lastName}`.trim());
          setShowClientDropdown(false);
        }}
      />
    </Dialog>
  );
}
